import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronLeft, MapPin, Cpu, ShieldAlert, CheckCircle2, CircleDashed } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Panel } from "@/components/Panel";
import { RiskBadge } from "@/components/RiskBadge";
import { Button } from "@/components/ui/button";
import { formatTimestamp, RISK } from "@/data/mockData";
import { useSentinel } from "@/context/SentinelContext";

export default function IncidentDetail() {
  const { id } = useParams();
  const { getIncident } = useSentinel();
  const incident = getIncident(id);

  if (!incident) return <Navigate to="/incidents" replace />;

  const cfg = RISK[incident.level];
  const isHigh = incident.level === "HIGH";

  return (
    <DashboardLayout>
      {/* Breadcrumb */}
      <Link
        to="/incidents"
        className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-slate-400 transition-colors hover:text-slate-100"
        data-testid="incident-detail-back"
      >
        <ChevronLeft className="h-3.5 w-3.5" /> Incident History
      </Link>

      {/* Header */}
      <div className="mt-4 flex flex-col gap-4 rounded-lg border border-sentinel-border bg-sentinel-card p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div className="flex items-center gap-5">
          <div
            className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-lg"
            style={{ backgroundColor: cfg.tint, border: `1px solid ${cfg.border}` }}
          >
            <span className="font-mono text-2xl font-bold tabular" style={{ color: cfg.hex }}>
              {incident.score}
            </span>
            <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">score</span>
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-xl font-semibold tracking-tight text-slate-100">
                {incident.eventType}
              </h1>
              <RiskBadge level={incident.level} label={incident.rawLevel} />
            </div>
            <p className="mt-1 font-mono text-xs text-slate-500">
              {incident.id} · {formatTimestamp(incident.timestamp)}
            </p>
          </div>
        </div>
        {isHigh && incident.escalation === "Active" && (
          <Button asChild className="bg-red-600 text-white hover:bg-red-500">
            <Link to="/emergency" data-testid="incident-view-emergency">
              <ShieldAlert className="mr-1.5 h-4 w-4" /> View Emergency State
            </Link>
          </Button>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-3">
        {/* Left: AI + sensors */}
        <div className="space-y-4 md:space-y-5 lg:col-span-2">
          <Panel
            title="AI Analysis"
            data-testid="incident-ai-explanation"
            action={
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-600">
                {incident.source === "supabase" ? "Live record" : "Demonstration output"}
              </span>
            }
          >
            <div className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-sentinel-border bg-sentinel-accent/10">
                <Cpu className="h-4 w-4 text-sentinel-accent" />
              </span>
              <div className="min-w-0 flex-1 space-y-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                    AI Context
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-200">
                    {incident.aiContext ||
                      "No AI analysis is attached to this incident record yet."}
                  </p>
                </div>
                <div className="border-t border-sentinel-border pt-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                    Recommended Action
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-200">
                    {incident.recommendedAction ||
                      (incident.level === "HIGH"
                        ? "Verify the user's safety."
                        : "No action required.")}
                  </p>
                </div>
              </div>
            </div>
          </Panel>

          <Panel title="Sensor / Event Context" data-testid="incident-sensor-context" contentClassName="p-0">
            <ul className="divide-y divide-sentinel-border">
              {incident.sensors.map((s) => (
                <li key={s.sensor} className="flex items-center justify-between px-5 py-3.5">
                  <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">
                    {s.sensor}
                  </span>
                  <span className="text-sm text-slate-200">{s.reading}</span>
                </li>
              ))}
            </ul>
          </Panel>
        </div>

        {/* Right: meta */}
        <div className="space-y-4 md:space-y-5">
          <Panel title="Classification" data-testid="incident-classification">
            <div className="space-y-3">
              <Meta label="Risk classification" value={incident.rawLevel || cfg.label} valueColor={cfg.hex} />
              <Meta
                label="Risk score"
                value={incident.scoreIsDerived ? `${incident.score} / 100 (derived)` : `${incident.score} / 100`}
              />
              <Meta label="Event type" value={incident.eventType} />
              <Meta label="Timestamp" value={formatTimestamp(incident.timestamp)} />
              <Meta label="Status" value={incident.status} />
            </div>
          </Panel>

          <Panel title="Location Availability" data-testid="incident-location">
            {incident.locationAvailable ? (
              <div>
                <div className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 text-sentinel-accent" />
                  <div>
                    <p className="text-sm text-slate-100">{incident.location.label}</p>
                    <p className="mt-0.5 font-mono text-xs text-slate-400">{incident.location.coordinates}</p>
                    <p className="mt-0.5 font-mono text-[11px] text-slate-500">Accuracy {incident.location.accuracy}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="flex items-center gap-2 text-sm text-slate-400">
                <CircleDashed className="h-4 w-4 text-slate-500" />
                Location unavailable at time of event.
              </p>
            )}
          </Panel>

          <Panel title="Response State" data-testid="incident-response-state">
            <div className="space-y-4">
              <StateRow
                label="User verification"
                value={incident.verification}
                good={/confirmed|not required|cancelled/i.test(incident.verification)}
                warn={/awaiting|no response/i.test(incident.verification)}
              />
              <StateRow
                label="Emergency escalation"
                value={incident.escalation}
                good={/not triggered|aborted/i.test(incident.escalation)}
                danger={/active/i.test(incident.escalation)}
              />
            </div>
          </Panel>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Meta({ label, value, valueColor }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">{label}</span>
      <span className="text-right text-sm font-medium" style={valueColor ? { color: valueColor } : { color: "#F1F5F9" }}>
        {value}
      </span>
    </div>
  );
}

function StateRow({ label, value, good, warn, danger }) {
  const color = danger ? "#EF4444" : warn ? "#F59E0B" : good ? "#10B981" : "#94A3B8";
  const Icon = good ? CheckCircle2 : CircleDashed;
  return (
    <div>
      <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">{label}</p>
      <p className="mt-1.5 flex items-center gap-2 text-sm font-medium" style={{ color }}>
        <Icon className="h-4 w-4" />
        {value}
      </p>
    </div>
  );
}
